/**
 * webauthn-errors/forensic-containment-messages.ts — Forensic Pause containment copy.
 *
 * Refusals whose cause is an OPEN SECURITY INCIDENT on the account, not a
 * mistake by the person holding the key. Split out of `messages.ts` under the
 * 300-line rule, following the same delegation the master / keypair-binding /
 * challenge-race / org-admin / session-lifecycle groups already use.
 *
 * Copy rules for everything in this file:
 *  - Red severity ONLY while the pause IS the cause (`CREDENTIAL_INCIDENT_EXCLUDED`).
 *    The post-lift hold codes (AC-8) are amber: the pause is over, so CLAUDE.md's
 *    red reservation no longer applies.
 *  - Say what is true about REVERSIBILITY. Containment is bounded by the incident;
 *    copy that reads like permanent loss is inaccurate and frightening.
 *  - Never imply the check proved anything about WHO was holding the key. It
 *    proves possession, freshly, and nothing more (§Friction Rule clause 2).
 */

import type { WebAuthnUserMessage } from "./types";

export const FORENSIC_CONTAINMENT_ERROR_MESSAGES: Record<string, WebAuthnUserMessage> = {
  /**
   * #3082 / AC-96. Distinct from `CREDENTIAL_REVOKED`: nothing has been removed
   * from the account and nothing here is permanent — the key is held out of use
   * only while an incident that named it is open. The copy therefore must not
   * read as "gone", and it points at both routes back in, because the refusal
   * fires on either (another registered key, or the recovery the owner set up).
   */
  CREDENTIAL_INCIDENT_EXCLUDED: {
    title:       "Security key held out of use",
    description: "A security incident on this account is still open, and this key is one of the keys it named. Use another key you have registered, or complete the recovery you set up. After the incident closes, this key is held until you clear it with another key.",
    retryable:   false,
    severity:    "red",
  },
  /** AC-7: the pause-time check could not be read, so the key is refused for
   *  now. Every key fails the same read, so the copy says retry, not "another key". */
  CREDENTIAL_INCIDENT_CHECK_UNAVAILABLE: {
    title:       "Couldn't check this key",
    description: "A security incident on this account is still open and we couldn't finish checking this key against it. Try again in a few minutes.",
    retryable:   true,
    severity:    "red",
  },
  /**
   * AC-8 (#3099). The pause has LIFTED, so this is not containment: AMBER,
   * never red. The key stays held until cleared with a different key.
   */
  CREDENTIAL_INCIDENT_HELD: {
    title:       "Passkey on hold",
    description: "This passkey is on hold after a security incident. Use another passkey, or clear the hold from your passkey list.",
    retryable:   false,
    severity:    "amber",
  },
  SAME_CREDENTIAL: {
    title:       "Use a different passkey",
    description: "A passkey on hold can't clear its own hold. Confirm with a different passkey.",
    retryable:   true,
    severity:    "amber",
  },
  HELD_MASTER_USE_TRANSFER: {
    title:       "Move master first",
    description: "This passkey is your master passkey. Move master to another passkey first, then clear the hold with it.",
    retryable:   false,
    severity:    "amber",
  },
  NO_OTHER_CREDENTIAL: {
    title:       "No other passkey available",
    description: "You need another passkey that is not on hold to clear this one. You can remove it instead.",
    retryable:   false,
    severity:    "amber",
  },
};
